import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InfractionList } from '../../../components/InfractionList.js';
import {
  useInfractions,
  usePardonInfraction,
  type InfractionEntry,
  type InfractionsFilters,
} from '../../../lib/infractionsQueries.js';
import { useGuildConfig } from '../../../lib/auditQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { UserPicker } from '../../../components/UserPicker.js';

export const Route = createFileRoute('/guilds/$guildId/infractions')({
  component: InfractionsPage,
});

function InfractionsPage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const [filters, setFilters] = useState<InfractionsFilters>({});
  const guildConfig = useGuildConfig(guildId);
  const infractions = useInfractions(guildId, filters);
  const pardon = usePardonInfraction(guildId);

  // Operator may be Mod (read-only) or Admin (can pardon). We don't have a
  // direct "myRole" endpoint; for now expose pardon to everyone reaching
  // this page — the API enforces requireGuildAdmin and shows 403 otherwise.
  const canPardon = true;
  const rows: InfractionEntry[] = infractions.data?.pages.flatMap((p) => p.items) ?? [];

  const handlePardon = (entry: InfractionEntry) => {
    if (!confirm(t('infractions.pardonConfirm', { id: entry.id }))) return;
    pardon.mutate(entry.id);
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="border-b border-zinc-800">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
          <Link to="/" className="shrink-0">
            <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
          </Link>
          <Breadcrumbs
            items={[
              { label: t('app.name'), to: '/' },
              {
                label: guildConfig.data?.name ?? guildId,
                to: '/guilds/$guildId',
                params: { guildId },
              },
              { label: t('infractions.title') },
            ]}
          />
          <div className="ml-auto">
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-6xl grid-cols-12 gap-6 px-6 py-8">
        <aside className="col-span-12 space-y-6 lg:col-span-3">
          <Section title={t('infractions.section.filter')}>
            <FilterField label={t('infractions.filter.userId')}>
              <UserPicker
                guildId={guildId}
                value={filters.userId ?? ''}
                onChange={(id) => {
                  setFilters((f) => ({ ...f, userId: id || undefined }));
                }}
              />
            </FilterField>
            <FilterField label={t('infractions.filter.type')}>
              <select
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
                value={filters.type ?? ''}
                onChange={(e) => {
                  setFilters((f) => ({
                    ...f,
                    type: (e.target.value || undefined) as InfractionsFilters['type'],
                  }));
                }}
              >
                <option value="">{t('infractions.filter.typeAll')}</option>
                <option value="warn">warn</option>
                <option value="timeout">timeout</option>
                <option value="kick">kick</option>
                <option value="ban">ban</option>
              </select>
            </FilterField>
            <FilterField label={t('infractions.filter.status')}>
              <select
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-2 py-1.5 text-sm"
                value={filters.active === undefined ? '' : filters.active ? 'active' : 'pardoned'}
                onChange={(e) => {
                  const v = e.target.value;
                  setFilters((f) => ({
                    ...f,
                    active: v === '' ? undefined : v === 'active',
                  }));
                }}
              >
                <option value="">{t('infractions.filter.statusAll')}</option>
                <option value="active">{t('infractions.status.active')}</option>
                <option value="pardoned">{t('infractions.status.pardoned')}</option>
              </select>
            </FilterField>
            <button
              type="button"
              onClick={() => {
                setFilters({});
              }}
              className="mt-2 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800"
            >
              {t('audit.filter.reset')}
            </button>
          </Section>
        </aside>

        <section className="col-span-12 lg:col-span-9">
          {infractions.isLoading ? (
            <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
              {t('common.loading')}
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-md border border-zinc-800 bg-zinc-900/40 p-6 text-sm text-zinc-400">
              {t('infractions.empty')}
            </div>
          ) : (
            <>
              <InfractionList
                guildId={guildId}
                rows={rows}
                canPardon={canPardon}
                onPardon={handlePardon}
                pardonPendingId={pardon.isPending ? pardon.variables : null}
              />
              <div className="mt-4 flex justify-center">
                {infractions.hasNextPage ? (
                  <button
                    type="button"
                    disabled={infractions.isFetchingNextPage}
                    onClick={() => {
                      void infractions.fetchNextPage();
                    }}
                    className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-1.5 text-sm hover:bg-zinc-800 disabled:opacity-50"
                  >
                    {infractions.isFetchingNextPage
                      ? t('common.loadingMore')
                      : t('common.loadMore')}
                  </button>
                ) : (
                  <span className="text-xs text-zinc-500">{t('common.endOfList')}</span>
                )}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-400">{title}</h3>
      <div className="space-y-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
        {children}
      </div>
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-zinc-400">{label}</span>
      {children}
    </label>
  );
}
